import { Link } from "react-router-dom";
import { motion } from "@/lib/motion-lite";
import { ArrowRight, CheckCircle2, HeartPulse, ShieldCheck, MapPin, FileDown, Globe, Trophy, Users } from "lucide-react";
import { HealthRing } from "@/components/panel/Insights";

const BARS = [78, 92, 85, 61, 96, 88, 73, 94, 90, 82, 97, 86];

function MockPanel({ t }) {
  return (
    <div className="relative rounded-3xl border border-border bg-card p-4 shadow-2xl shadow-primary/10 md:p-5" data-testid="account-mock-panel" aria-hidden="true">
      <div className="flex items-center justify-between border-b border-border pb-3">
        <div className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full bg-red-400" /><span className="h-2.5 w-2.5 rounded-full bg-amber-400" /><span className="h-2.5 w-2.5 rounded-full bg-emerald-400" /></div>
        <span className="rounded-full bg-primary/10 px-2.5 py-0.5 text-[10px] font-800 uppercase tracking-wide text-primary">{t("acct.mockLive")}</span>
      </div>
      <div className="mt-4 grid grid-cols-3 gap-2">
        {[["↓ 586", "Mbps"], ["↑ 58", "Mbps"], ["9", "ms"]].map(([v, u]) => (
          <div key={v} className="rounded-xl bg-secondary/70 p-2.5"><div className="font-mono text-lg font-800 leading-none">{v}</div><div className="mt-1 text-[10px] text-muted-foreground">{u}</div></div>
        ))}
      </div>
      <div className="mt-3 grid gap-3 sm:grid-cols-[auto_1fr]">
        <div className="flex items-center gap-3 rounded-xl border border-border p-3">
          <HealthRing score={87} size={64} stroke={7} />
          <div><div className="text-[10px] font-800 uppercase tracking-wide text-muted-foreground">Health Score</div><div className="text-sm font-800 text-emerald-500">{t("acct.mockGreat")}</div></div>
        </div>
        <div className="rounded-xl border border-border p-3">
          <div className="flex items-center justify-between text-[10px] font-800 uppercase tracking-wide text-muted-foreground"><span>{t("acct.mockPlan")}</span><span className="text-emerald-500">92%</span></div>
          <div className="mt-2 flex h-14 items-end gap-1">
            {BARS.map((b, i) => <span key={i} style={{ height: `${b}%`, animationDelay: `${i * 60}ms` }} className={`mock-bar flex-1 rounded-sm ${b < 80 ? "bg-amber-400" : "bg-primary"}`} />)}
          </div>
          <div className="mt-1 border-t border-dashed border-destructive/60 pt-1 text-[10px] text-muted-foreground">600 Mbps · {t("acct.mockContract")}</div>
        </div>
      </div>
      <div className="mt-3 flex items-center gap-2 rounded-xl bg-amber-500/10 px-3 py-2 text-xs font-600 text-amber-700 dark:text-amber-400">
        <Trophy className="h-4 w-4 shrink-0" /> {t("acct.mockPercentile")}
      </div>
    </div>
  );
}

const BENEFITS = [
  ["health", HeartPulse], ["plan", ShieldCheck], ["area", MapPin], ["reports", FileDown], ["profile", Globe],
];

export function AccountSection({ t, lp, users }) {
  return (
    <section className="relative overflow-hidden border-y border-border" data-testid="landing-account">
      <div className="absolute inset-0 -z-10 bg-[radial-gradient(70%_60%_at_80%_20%,hsl(var(--primary)/0.14),transparent_70%)]" />
      <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 py-20 md:px-8 lg:grid-cols-2">
        <div>
          <span className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/5 px-3.5 py-1 text-xs font-700 uppercase tracking-wide text-primary">{t("acct.badge")}</span>
          <h2 className="mt-4 font-head text-3xl font-800 leading-tight md:text-5xl">{t("acct.title")}</h2>
          <p className="mt-4 max-w-xl text-lg text-muted-foreground">{t("acct.lead")}</p>
          <ul className="mt-8 space-y-3">
            {BENEFITS.map(([k, Icon], i) => (
              <motion.li key={k} initial={{ opacity: 0, x: -12 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }} className="flex items-start gap-3" data-testid={`account-benefit-${k}`}>
                <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary"><Icon className="h-4 w-4" /></span>
                <div><div className="font-700">{t(`acct.b_${k}T`)}</div><div className="text-sm text-muted-foreground">{t(`acct.b_${k}D`)}</div></div>
              </motion.li>
            ))}
          </ul>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Link to={lp("/register")} data-testid="account-register-btn" className="hero-cta inline-flex items-center gap-2 rounded-full bg-primary px-7 py-3.5 text-base font-800 text-primary-foreground transition-transform hover:-translate-y-0.5">
              {t("acct.cta")} <ArrowRight className="h-5 w-5" />
            </Link>
            <Link to={lp("/register?google=1")} data-testid="account-google-btn" className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-6 py-3.5 text-base font-600 transition-colors hover:bg-accent">
              <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true"><path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.6l6.7-6.7C35.6 2.6 30.2 0 24 0 14.6 0 6.5 5.4 2.6 13.3l7.8 6C12.3 13.7 17.7 9.5 24 9.5z"/><path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-2.8-.4-4.1H24v7.8h12.7c-.3 2.1-1.7 5.3-4.8 7.4l7.4 5.7c4.4-4.1 7.2-10.1 7.2-16.8z"/><path fill="#FBBC05" d="M10.4 28.7A14.6 14.6 0 0 1 9.6 24c0-1.6.3-3.2.8-4.7l-7.8-6A24 24 0 0 0 0 24c0 3.9.9 7.5 2.6 10.7l7.8-6z"/><path fill="#34A853" d="M24 48c6.5 0 11.9-2.1 15.9-5.8l-7.4-5.7c-2 1.4-4.7 2.4-8.5 2.4-6.3 0-11.7-4.2-13.6-10.2l-7.8 6C6.5 42.6 14.6 48 24 48z"/></svg>
              {t("acct.google")}
            </Link>
          </div>
          <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-muted-foreground">
            <span className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-success" /> {t("acct.free")}</span>
            <span className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-success" /> {t("acct.noCard")}</span>
            {users > 0 && <span className="flex items-center gap-1.5" data-testid="account-social-proof"><Users className="h-4 w-4 text-primary" /> {t("acct.joined").replace("{n}", users.toLocaleString())}</span>}
          </div>
        </div>
        <MockPanel t={t} />
      </div>
    </section>
  );
}
